import { CSSProperties, useState } from "react";
import { itx, noItx } from "../translationItx/itx";
import BaseDialog from "./BaseDialog";
import { EventKey } from "../eventLogger/EventKeys";

// Unterstützte Export-Zielformate.
type ExportFormat = "xml" | "csv";

// Spaltenlayout für den CSV-Export (entspricht ETS-ähnlichen Varianten).
type CsvFormat = "3_1" | "1_3" | "1_1" | "3_3";

// Unterstützte Trennzeichen für CSV.
type CsvSeparator = "tab" | "comma" | "semicolon";

// Mapping für Unicode-Zeichen aus dem Windows-1252 Sonderbereich (0x80–0x9F).
// Damit können wir CSV gezielt als ANSI/Windows-1252 ausgeben.
const WINDOWS_1252_SPECIAL_CHAR_TO_BYTE: Record<string, number> = {
  "€": 0x80, // no itx
  "‚": 0x82, // no itx
  ƒ: 0x83, // no itx
  "„": 0x84, // no itx
  "…": 0x85, // no itx
  "†": 0x86, // no itx
  "‡": 0x87, // no itx
  ˆ: 0x88, // no itx
  "‰": 0x89, // no itx
  Š: 0x8a, // no itx
  "‹": 0x8b, // no itx
  Œ: 0x8c, // no itx
  Ž: 0x8e, // no itx
  "‘": 0x91, // no itx
  "’": 0x92, // no itx
  "“": 0x93, // no itx
  "”": 0x94, // no itx
  "•": 0x95, // no itx
  "–": 0x96, // no itx
  "—": 0x97, // no itx
  "˜": 0x98, // no itx
  "™": 0x99, // no itx
  š: 0x9a, // no itx
  "›": 0x9b, // no itx
  œ: 0x9c, // no itx
  ž: 0x9e, // no itx
  Ÿ: 0x9f, // no itx
};

// Interne Zeilenstruktur für den CSV-Aufbau; je nach Format wird unterschiedlich gemappt.
type GroupAddressExportRow = {
  groupName: string;
  address: string;
  mainName: string;
  middleName: string;
  subName: string;
  mainAdress: string;
  middleAdress: string;
  subAdress: string;
  central: string;
  unfiltered: string;
  description: string;
  datapointType: string;
  security: string;
};

export default function ExportGroupAdressesDialog() {
  // Dialogzustand (Format, CSV-Layout, Separator, Header ja/nein).
  const [exportFormat, setExportFormat] = useState<ExportFormat>("xml"); // no itx
  const [csvFormat, setCsvFormat] = useState<CsvFormat>("3_1"); // no itx
  const [csvSeparator, setCsvSeparator] = useState<CsvSeparator>("tab"); // no itx
  const [withHeader, setWithHeader] = useState<boolean>(true);

  // Projektnamen aus Metadaten für Dateinamen übernehmen.
  const projectNameRaw = state.projectMetadata?.name || itx("ExportGroupAdressesDialog.projectname", "ProjectName");

  // Ungültige Dateiname-Zeichen ersetzen.
  const safeProjectName =
    projectNameRaw.replace(/[<>:"/\\|?*]+/g, "_").trim() || itx("ExportGroupAdressesDialog.projectname", "ProjectName");

  // Formatiert Datums-/Zeitteile zweistellig (z. B. 07).
  function formatDatePart(value: number): string {
    return String(value).padStart(2, "0");
  }

  // Erzeugt einen stabilen Zeitstempel für Dateinamen.
  function getTimestamp(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = formatDatePart(now.getMonth() + 1);
    const d = formatDatePart(now.getDate());
    const hh = formatDatePart(now.getHours());
    const mm = formatDatePart(now.getMinutes());
    const ss = formatDatePart(now.getSeconds());
    return `${y}${m}${d}_${hh}${mm}${ss}`;
  }

  // Escaping für XML-Attribute/Content.
  function escapeXml(value: string): string {
    return value
      .replace(/&/g, "&amp;") // no itx
      .replace(/</g, "&lt;") // no itx
      .replace(/>/g, "&gt;") // no itx
      .replace(/\"/g, "&quot;") // no itx
      .replace(/'/g, "&apos;"); // no itx
  }

  // CSV-Escaping: Werte werden vollständig gequotet.
  // Hinweis: separator wird aktuell nicht aktiv genutzt, bleibt aber für Erweiterungen im Interface.
  function escapeCsv(value: string, separator: string): string {
    void separator;
    if (value == undefined || value == null) {
      return '""';
    }

    const stringValue = String(value);
    return `"${stringValue}"`;
  }

  // Kodiert Text explizit nach Windows-1252 (ANSI), da das Zielprogramm dies erwartet.
  // Nicht abbildbare Zeichen werden als '?' ersetzt.
  function encodeWindows1252(text: string): Uint8Array {
    const bytes: number[] = [];

    for (const char of text) {
      const codePoint = char.codePointAt(0) || 0;

      if (codePoint <= 0x7f) {
        bytes.push(codePoint);
        continue;
      }

      if (codePoint >= 0xa0 && codePoint <= 0xff) {
        bytes.push(codePoint);
        continue;
      }

      const mappedByte = WINDOWS_1252_SPECIAL_CHAR_TO_BYTE[char];
      if (mappedByte != undefined) {
        bytes.push(mappedByte);
        continue;
      }

      bytes.push(0x3f);
    }

    return new Uint8Array(bytes);
  }

  // Robustes Parsen einzelner Adressbestandteile mit Begrenzung auf gültige KNX-Ranges.
  function parsePartToNumber(value: string | undefined, min: number, max: number): number {
    const parsedValue = Number.parseInt(value || "0", 10);
    if (!Number.isFinite(parsedValue) || Number.isNaN(parsedValue)) {
      return min;
    }
    return Math.max(min, Math.min(max, parsedValue));
  }

  // KNX Gruppenadresse als 16-bit Ganzzahl (5/3/8 Bit Schema) berechnen.
  function calculateKnxGroupAddress(mainGroup: number, middleGroup: number, subGroup: number): number {
    return (mainGroup << 11) + (middleGroup << 8) + subGroup;
  }

  // ETS/Export-Sonderfall: RangeStart 0 wird als 1 ausgegeben.
  function normalizeRangeStart(value: number): number {
    return value === 0 ? 1 : value;
  }

  // Zerlegt eine 3-stufige Gruppenadresse in numerische Teile.
  function parseFullAddress(fullAddress: string): { mainGroup: number; middleGroup: number; subGroup: number } {
    const parts = (fullAddress || "").split("/");
    return {
      mainGroup: parsePartToNumber(parts[0], 0, 31),
      middleGroup: parsePartToNumber(parts[1], 0, 7),
      subGroup: parsePartToNumber(parts[2], 0, 255),
    };
  }

  // Baut das XML im KNX GroupAddress-Export-Format auf und lädt es herunter.
  function createXmlFile(): boolean {
    const rows = [
      `<?xml version="1.0" encoding="utf-8" standalone="yes"?>`,
      `<GroupAddress-Export xmlns="http://knx.org/xml/ga-export/01">`,
    ];
    state.addresses.sortBy("address").forEach((ga1) => {
      if (ga1.children.size > 0) {
        const mainGroup = parsePartToNumber(ga1.address, 0, 31);
        const group1Start = normalizeRangeStart(calculateKnxGroupAddress(mainGroup, 0, 0));
        const group1End = calculateKnxGroupAddress(mainGroup, 7, 255);
        rows.push(
          `  <GroupRange Name="${escapeXml(ga1.nameOrTag || ga1.address)}" RangeStart="${group1Start}" RangeEnd="${group1End}">`,
        );

        ga1.children.sortBy("address").forEach((ga2) => {
          if (ga2.children.size > 0) {
            const middleGroup = parsePartToNumber(ga2.address, 0, 7);
            const group2Start = normalizeRangeStart(calculateKnxGroupAddress(mainGroup, middleGroup, 0));
            const group2End = calculateKnxGroupAddress(mainGroup, middleGroup, 255);
            rows.push(
              `    <GroupRange Name="${escapeXml(ga2.nameOrTag || ga2.address)}" RangeStart="${group2Start}" RangeEnd="${group2End}">`,
            );

            ga2.children.sortBy("address").forEach((ga3) => {
              // Wird aktuell nicht im XML geschrieben, bleibt aber als validierte Ableitung erhalten.
              const parsedAddress = parseFullAddress(ga3.fullAddress || "");
              const groupAddressValue = calculateKnxGroupAddress(
                parsedAddress.mainGroup,
                parsedAddress.middleGroup,
                parsedAddress.subGroup,
              );
              void groupAddressValue;
              rows.push(
                `      <GroupAddress ` +
                  `Name="${escapeXml(ga3.nameOrTag || ga3.fullAddress)}" ` +
                  `Address="${escapeXml(ga3.fullAddress)}" ` +
                  `DPTs="${escapeXml(ga3.dpt.toUpperCase())}"` +
                  `/>`,
              );
            });

            rows.push(`    </GroupRange>`);
          }
        });

        rows.push(`  </GroupRange>`);
      }
    });

    rows.push(`</GroupAddress-Export>`);

    const xmlContent = rows.join("\n") + "\n";

    // XML bleibt UTF-8.
    downloadFile(xmlContent, "application/xml;charset=utf-8;", "xml", `${safeProjectName}GroupAdressesXML`); // no itx
    return true;
  }

  // Baut den kompletten CSV-Text entsprechend der gewählten Export-Variante auf.
  function createCsvContent(): string {
    // Standardseparator: Semikolon (deutsche CSV-Konvention), falls nichts anderes gewählt wurde.
    let separatorValue = ";";
    if (csvSeparator == "comma") {
      separatorValue = ",";
    } else if (csvSeparator == "tab") {
      separatorValue = "\t";
    }

    let headers: string[] = [];
    // rowMapper definiert pro CSV-Format die Spaltenreihenfolge.
    let rowMapper: (row: GroupAddressExportRow) => string[] = () => [];

    if (csvFormat == "3_1") {
      headers = [
        itx("exportGroupAddressesDialog.main", "Main"),
        itx("exportGroupAddressesDialog.middle", "Middle"),
        itx("exportGroupAddressesDialog.sub", "Sub"),
        itx("exportGroupAddressesDialog.address", "Address"),
        itx("exportGroupAddressesDialog.central", "Central"),
        itx("exportGroupAddressesDialog.unfiltered", "Unfiltered"),
        itx("exportGroupAddressesDialog.description", "Description"),
        itx("exportGroupAddressesDialog.datapointtype", "DatapointType"),
        itx("exportGroupAddressesDialog.security", "Security"),
      ];
      rowMapper = (row) => [
        row.mainName,
        row.middleName,
        row.subName,
        row.address,
        row.central,
        row.unfiltered,
        row.description,
        row.datapointType,
        row.security,
      ];
    } else if (csvFormat == "1_3") {
      headers = [
        itx("exportGroupAddressesDialog.group name", "Group name"),
        itx("exportGroupAddressesDialog.main", "Main"),
        itx("exportGroupAddressesDialog.middle", "Middle"),
        itx("exportGroupAddressesDialog.sub", "Sub"),
        itx("exportGroupAddressesDialog.central", "Central"),
        itx("exportGroupAddressesDialog.unfiltered", "Unfiltered"),
        itx("exportGroupAddressesDialog.description", "Description"),
        itx("exportGroupAddressesDialog.datapointtype", "DatapointType"),
        itx("exportGroupAddressesDialog.security", "Security"),
      ];
      rowMapper = (row) => [
        row.groupName,
        row.mainAdress,
        row.middleAdress,
        row.subAdress,
        row.central,
        row.unfiltered,
        row.description,
        row.datapointType,
        row.security,
      ];
    } else if (csvFormat == "1_1") {
      headers = [
        itx("exportGroupAddressesDialog.group name", "Group name"),
        itx("exportGroupAddressesDialog.address", "Address"),
        itx("exportGroupAddressesDialog.central", "Central"),
        itx("exportGroupAddressesDialog.unfiltered", "Unfiltered"),
        itx("exportGroupAddressesDialog.description", "Description"),
        itx("exportGroupAddressesDialog.datapointtype", "DatapointType"),
        itx("exportGroupAddressesDialog.security", "Security"),
      ];
      rowMapper = (row) => [
        row.groupName,
        row.address,
        row.central,
        row.unfiltered,
        row.description,
        row.datapointType,
        row.security,
      ];
    } else {
      headers = [
        itx("exportGroupAddressesDialog.main", "Main"),
        itx("exportGroupAddressesDialog.middle", "Middle"),
        itx("exportGroupAddressesDialog.sub", "Sub"),
        itx("exportGroupAddressesDialog.main", "Main"),
        itx("exportGroupAddressesDialog.middle", "Middle"),
        itx("exportGroupAddressesDialog.sub", "Sub"),
        itx("exportGroupAddressesDialog.central", "Central"),
        itx("exportGroupAddressesDialog.unfiltered", "Unfiltered"),
        itx("exportGroupAddressesDialog.description", "Description"),
        itx("exportGroupAddressesDialog.datapointtype", "DatapointType"),
        itx("exportGroupAddressesDialog.security", "Security"),
      ];
      rowMapper = (row) => [
        row.mainName,
        row.middleName,
        row.subName,
        row.mainAdress,
        row.middleAdress,
        row.subAdress,
        row.central,
        row.unfiltered,
        row.description,
        row.datapointType,
        row.security,
      ];
    }
    const rows: GroupAddressExportRow[] = [];
    const lines: string[] = [];

    // Optionale Kopfzeile.
    if (withHeader) {
      lines.push(headers.map((h) => escapeCsv(h, separatorValue)).join(separatorValue));
    }

    // Baumstruktur (L1/L2/L3) in flache Zeilenliste umwandeln.
    state.addresses.sortBy("address").forEach((ga1) => {
      rows.push({
        groupName: ga1.nameOrTag || ga1.address,
        address: ga1.fullAddress + "/-/-" || ga1.address + "/-/-", // no itx
        mainName: ga1.nameOrTag || ga1.address,
        middleName: "",
        subName: "",
        mainAdress: ga1.address,
        middleAdress: "",
        subAdress: "",
        central: "",
        unfiltered: "",
        description: "",
        datapointType: "",
        security: "Auto", // no itx
      });
      ga1.children.sortBy("address").forEach((ga2) => {
        rows.push({
          groupName: ga2.nameOrTag || ga2.address,
          address: ga2.fullAddress + "/-" || ga2.address + "/-", // no itx
          mainName: "",
          middleName: ga2.nameOrTag || ga2.address,
          subName: "",
          mainAdress: ga1.address,
          middleAdress: ga2.address,
          subAdress: "",
          central: "",
          unfiltered: "",
          description: "",
          datapointType: "",
          security: "Auto", // no itx
        });
        ga2.children.sortBy("address").forEach((ga3) => {
          rows.push({
            groupName: ga3.nameOrTag || ga3.address,
            address: ga3.fullAddress || ga3.address,
            mainName: "",
            middleName: "",
            subName: ga3.nameOrTag || ga3.address,
            mainAdress: ga1.address,
            middleAdress: ga2.address,
            subAdress: ga3.address,
            central: "",
            unfiltered: "",
            description: "",
            datapointType: ga3.dpt.toUpperCase(),
            security: "Auto", // no itx
          });
        });
      });
    });

    rows.forEach((row) => {
      // Jede Zeile entsprechend Format-Mapping serialisieren.
      lines.push(
        rowMapper(row)
          .map((c) => escapeCsv(c, separatorValue))
          .join(separatorValue),
      );
    });

    // CRLF für maximale Windows-/Tool-Kompatibilität.
    return lines.join("\r\n");
  }

  // Einheitliche Download-Helferfunktion für Text/Binärinhalte.
  function downloadFile(content: BlobPart, mimeType: string, fileExtension: string, fileNameWithoutExtension?: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const defaultName = `group_addresses_${getTimestamp()}`;
    link.download = `${fileNameWithoutExtension || defaultName}.${fileExtension}`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Einstieg vom Dialog-OK-Button: je nach Auswahl XML oder CSV erzeugen.
  function handleExport(): boolean {
    state.eventLogger.addEvent(EventKey.GroupAddressesExported, {
      format: exportFormat,
      exportedCount: state.addresses.size,
    });
    if (exportFormat == "xml") {
      return createXmlFile();
    }

    const csvContent = createCsvContent();

    // CSV gezielt in Windows-1252 ausgeben (nicht UTF-8), passend zum Zielprogramm.
    const csvContentWindows1252 = encodeWindows1252(csvContent);

    // Auf ArrayBuffer kopieren, damit BlobPart-Typen in TS strikt eingehalten werden.
    const csvBuffer = new ArrayBuffer(csvContentWindows1252.byteLength);
    new Uint8Array(csvBuffer).set(csvContentWindows1252);

    downloadFile(
      csvBuffer,
      "text/csv;charset=windows-1252;", // no itx
      "csv", // no itx
      `${safeProjectName}GroupAdressesCSV${csvFormat}`,
    );

    return true;
  }

  // Visuelle Karten-Optik für die Konfigurationsblöcke.
  const cardStyle: CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  return (
    <BaseDialog
      title={itx("ExportGroupAdressesDialog.gruppenadressenexportieren", "Gruppenadressen exportieren")}
      onOk={handleExport}
      okCaption={itx("exportGroupAddressesDialog.export", "Exportieren")}
      abortCaption={itx("exportGroupAddressesDialog.cancel", "Abbrechen")}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: "580px" }}>
        {/* Grundauswahl: Export als XML oder CSV */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: "16px" }}>
            {itx("exportGroupAddressesDialog.format", "Ausgabeformat")}
          </div>
          <div style={{ fontSize: "12px", opacity: 0.8 }}>
            {itx(
              "exportGroupAddressesDialog.formatHint",
              "Wählen Sie, ob der Export als XML oder CSV erstellt werden soll.",
            )}
          </div>
          <select
            value={exportFormat}
            onChange={(ev) => {
              setExportFormat(ev.target.value as ExportFormat);
            }}
            style={{ width: "100%", maxWidth: "280px", marginTop: "4px" }}
          >
            <option
              value="xml" // no itx
            >
              {noItx("XML")}
            </option>
            <option
              value="csv" // no itx
            >
              {noItx("CSV")}
            </option>
          </select>
        </div>

        {exportFormat == "csv" && (
          // CSV-spezifische Optionen werden nur bei CSV eingeblendet.
          <div style={cardStyle}>
            <div
              style={{
                marginBottom: "6px",
                paddingBottom: "8px",
                fontWeight: 700,
                fontSize: "16px",
                borderBottom: "1px solid #ddd",
              }}
            >
              {itx("exportGroupAddressesDialog.csvOptions", "CSV-Optionen")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {/* Layout-Variante der CSV-Spalten */}
                <div style={{ fontWeight: 600, fontSize: "13px" }}>
                  {itx("exportGroupAddressesDialog.csvFormat", "Format")}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.8 }}>
                  {itx(
                    "exportGroupAddressesDialog.csvFormatHint",
                    "Bestimmt, wie Namen und Adressen in Spalten angeordnet werden.",
                  )}
                </div>
                <select
                  value={csvFormat}
                  onChange={(ev) => {
                    setCsvFormat(ev.target.value as CsvFormat);
                  }}
                  style={{ width: "100%" }}
                >
                  <option
                    value="3_1" // no itx
                  >
                    {itx(
                      "exportGroupAddressesDialog.3ColumnsName1ColumnAddress",
                      "Drei Spalten Name (Haupt/Mittel/Untergruppe), eine Spalte Adresse",
                    )}
                  </option>
                  <option
                    value="1_3" // no itx
                  >
                    {itx(
                      "exportGroupAddressesDialog.1ColumnName3ColumnsAddress",
                      "Eine Spalte Gruppenname, drei Spalten Adresse (Haupt/Mittel/Untergruppe)",
                    )}
                  </option>
                  <option
                    value="1_1" // no itx
                  >
                    {itx("exportGroupAddressesDialog.NameAddress", "Name / Adresse")}
                  </option>
                  <option
                    value="3_3" // no itx
                  >
                    {itx(
                      "exportGroupAddressesDialog.3LevelName3LevelAddress",
                      "Haupt/Mittel/Untergruppe Name und Haupt/Mittel/Untergruppe Adresse",
                    )}
                  </option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {/* Trennzeichen der CSV-Datei */}
                <div style={{ fontWeight: 600, fontSize: "13px" }}>
                  {itx("exportGroupAddressesDialog.csvSeparator", "Separator")}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.8 }}>
                  {itx("exportGroupAddressesDialog.csvSeparatorHint", "Trennzeichen der CSV-Datei.")}
                </div>
                <select
                  value={csvSeparator}
                  onChange={(ev) => {
                    setCsvSeparator(ev.target.value as CsvSeparator);
                  }}
                  style={{ width: "100%", maxWidth: "280px" }}
                >
                  <option
                    value="tab" // no itx
                  >
                    {itx("exportGroupAddressesDialog.tab", "Tabulator")}
                  </option>
                  <option
                    value="comma" // no itx
                  >
                    {itx("exportGroupAddressesDialog.comma", "Komma")}
                  </option>
                  <option
                    value="semicolon" // no itx
                  >
                    {itx("exportGroupAddressesDialog.semicolon", "Semikolon")}
                  </option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: "2px", paddingTop: "8px", borderTop: "1px solid #ddd" }}>
              {/* Optionale Header-Zeile */}
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={withHeader}
                  onChange={(ev) => {
                    setWithHeader(ev.target.checked);
                  }}
                />
                {itx("ExportGroupAdressesDialog.mitKopfzeileExportieren", "Mit Kopfzeile exportieren")}
              </label>
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
